import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { api, imgSrc, waLink } from "../lib/api";

export default function CarDetail() {
  const { id } = useParams();
  const { t, fmtPrice, fmtNum } = useLang();
  const [car, setCar] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setActive(0);
    api.get(`/cars/${id}`).then((r) => setCar(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <main className="pt-40 pb-32 min-h-[60vh] text-center" data-testid="car-not-found">
        <p className="font-display italic text-3xl mb-8">{t("detail.notFound")}</p>
        <Link to="/auto" className="btn-solid">{t("detail.back")}</Link>
      </main>
    );

  if (!car) return <main className="min-h-screen" data-testid="car-loading" />;

  const images = car.images?.length ? car.images : [""];
  const label = (key, value, trKey) => trKey && t(`${trKey}.${value}`) !== `${trKey}.${value}` ? t(`${trKey}.${value}`) : value;

  const specs = [
    { k: t("f.year"), v: car.year },
    { k: t("f.km"), v: `${fmtNum(car.km)} km` },
    { k: t("f.fuel"), v: label("f", car.fuel, "fuel") },
    { k: t("f.transmission"), v: label("f", car.transmission, "tr") },
    { k: t("f.status"), v: car.sold ? t("badge.sold") : t(`status.${car.status}`) || car.status },
  ];

  const waMsg = (t("detail.waMsg") || "").replace("{car}", `${car.brand} ${car.model} (${car.year})`);

  return (
    <main className="pt-28 md:pt-36 pb-24" data-testid="car-detail-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <Link to="/auto" className="btn-line mb-10" data-testid="car-detail-back">
          <ArrowLeft size={15} />
          {t("detail.back")}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 xl:gap-16">
          {/* Gallery */}
          <div className="lg:col-span-7" data-testid="car-gallery">
            <div className="aspect-[4/3] overflow-hidden bg-paper3">
              <img key={active} src={imgSrc(images[active])} alt={`${car.brand} ${car.model}`} className="w-full h-full object-cover" />
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 mt-4 overflow-x-auto pb-1">
                {images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={`car-gallery-thumb-${i}`}
                    onClick={() => setActive(i)}
                    className={`w-24 h-16 flex-none overflow-hidden border-2 transition-colors ${i === active ? "border-rosso" : "border-transparent opacity-70 hover:opacity-100"}`}
                  >
                    <img src={imgSrc(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Panel */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <p className="eyebrow mb-3">
                {car.sold ? t("badge.sold") : t(`status.${car.status}`) || car.status} — {car.year}
              </p>
              <h1 className="display-title text-5xl md:text-6xl mb-6">
                {car.brand} <span className="italic text-rosso">{car.model}</span>
              </h1>
              <p className="font-mono text-3xl mb-8" data-testid="car-detail-price">{fmtPrice(car.price)}</p>

              <dl className="border-t border-ink/10" data-testid="car-detail-specs">
                {specs.map((s) => (
                  <div key={s.k} className="flex items-center justify-between py-3.5 border-b border-ink/10">
                    <dt className="font-mono text-[11px] uppercase tracking-[0.2em] text-fog">{s.k}</dt>
                    <dd className="font-body text-sm text-ink">{s.v}</dd>
                  </div>
                ))}
              </dl>

              {car.description && (
                <p className="text-smoke leading-relaxed mt-8" data-testid="car-detail-description">{car.description}</p>
              )}

              {!car.sold && (
                <a
                  href={waLink(waMsg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="car-detail-whatsapp"
                  className="mt-10 w-full inline-flex items-center justify-center gap-3 bg-whatsapp text-blu font-body text-sm uppercase tracking-[0.18em] px-8 py-5 hover:bg-blu hover:text-white transition-colors duration-300"
                >
                  {t("detail.wa")}
                  <ArrowUpRight size={16} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
